import { useMemo, useState, useRef } from 'react';
import { MediaDropRegion, profileImageUploader, type UploadItem } from '@arthelper/ui';
import { useQuery, useLazyQuery, useMutation, gql } from '@apollo/client';
import {
  Button,
  Input,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Label,
} from '@artstorefronts/ui';
import { Plus, Loader2, Edit2, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import AdminLayout from '../../../components/AdminLayout';
import { DateTimePicker } from '../../../components/DateTimePicker';

const ADMIN_CONTESTS_QUERY = gql`
  query AdminContests {
    adminContests {
      id
      name
      description
      thumbnailUrl
      status
      endDate
      postId
      post {
        id
        slug
        community {
          slug
        }
      }
      createdAt
      winners {
        id
        place
        userId
        commentId
        user {
          asfId
          firstName
          lastName
          displayName
          avatarUrl
          slug
        }
        comment {
          id
          content
        }
      }
    }
  }
`;

const ADMIN_CONTEST_QUERY = gql`
  query AdminContest($id: ID!) {
    adminContest(id: $id) {
      id
      name
      postId
      winners {
        id
        place
        userId
        commentId
        user {
          asfId
          firstName
          lastName
          displayName
          avatarUrl
          slug
        }
        comment {
          id
          content
        }
      }
    }
  }
`;

const GET_COMMUNITY_POST_BY_SLUG = gql`
  query GetCommunityPostBySlug($communitySlug: String!, $postSlug: String!) {
    communityPostBySlug(communitySlug: $communitySlug, postSlug: $postSlug) {
      id
      slug
    }
  }
`;

const ADMIN_CREATE_CONTEST = gql`
  mutation AdminCreateContest($input: AdminCreateContestInput!) {
    adminCreateContest(input: $input) {
      id
      name
    }
  }
`;

const ADMIN_UPDATE_CONTEST = gql`
  mutation AdminUpdateContest($input: AdminUpdateContestInput!) {
    adminUpdateContest(input: $input) {
      id
      name
    }
  }
`;

const ADMIN_DELETE_CONTEST = gql`
  mutation AdminDeleteContest($id: ID!) {
    adminDeleteContest(id: $id)
  }
`;

const UPLOAD_CONTEST_IMAGE = gql`
  mutation UploadContestImage($uploadId: ID!, $contentType: String) {
    uploadContestImage(uploadId: $uploadId, contentType: $contentType) {
      success
      url
      error
    }
  }
`;

const PROCESS_CONTEST_IMAGE = gql`
  mutation ProcessContestImage($uploadId: ID!, $key: String!, $contestId: ID) {
    processContestImage(uploadId: $uploadId, key: $key, contestId: $contestId) {
      success
      url
      error
    }
  }
`;

interface ContestWinner {
  id: string;
  place: number;
  userId: string;
  commentId: string;
  user: {
    asfId: string;
    firstName?: string | null;
    lastName?: string | null;
    displayName?: string | null;
    avatarUrl?: string | null;
    slug?: string | null;
  };
  comment: { id: string; content: string };
}

interface Contest {
  id: string;
  name: string;
  description: string | null;
  thumbnailUrl: string | null;
  status: string;
  endDate: string;
  postId: string | null;
  post: { id: string; slug: string | null; community: { slug: string } | null } | null;
  createdAt: string;
  winners: ContestWinner[];
}

interface ContestFormData {
  name: string;
  description: string;
  postUrl: string;
  endDate: Date | null;
  thumbnailUrl: string;
}

interface ParsedPostUrl {
  communitySlug: string;
  postSlug: string;
}

const PLACES = [1, 2, 3] as const;
const PLACE_LABELS: Record<number, string> = { 1: 'first-place', 2: 'second-place', 3: 'third-place' };

function getWinnerDisplayName(winner: ContestWinner): string {
  return (
    winner.user.displayName || [winner.user.firstName, winner.user.lastName].filter(Boolean).join(' ') || 'Unknown'
  );
}

function extractFirstImageSrc(html: string): string | null {
  const match = /<img[^>]+src=["']([^"']+)["']/i.exec(html);
  return match ? match[1] : null;
}

function parsePostUrl(url: string): ParsedPostUrl | null {
  try {
    const u = new URL(url.trim());
    // Matches paths like /community/some-slug/post/post-slug or /en/community/some-slug/post/post-slug
    const match = u.pathname.match(/\/(?:[a-z]{2}\/)?community\/([^/]+)\/post\/([^/]+)/);
    if (match) {
      return { communitySlug: match[1], postSlug: match[2] };
    }
  } catch {
    // Not a full URL — try matching just the path directly
    const match = url.trim().match(/\/(?:[a-z]{2}\/)?community\/([^/]+)\/post\/([^/]+)/);
    if (match) {
      return { communitySlug: match[1], postSlug: match[2] };
    }
  }
  return null;
}

const emptyForm = (): ContestFormData => ({
  name: '',
  description: '',
  postUrl: '',
  endDate: null,
  thumbnailUrl: '',
});

export default function ContestsPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');

  const [editContest, setEditContest] = useState<Contest | null>(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteContest, setDeleteContest] = useState<Contest | null>(null);
  const [formData, setFormData] = useState<ContestFormData>(emptyForm());
  const [resolvedPostId, setResolvedPostId] = useState<string | null>(null);
  const [resolvingPost, setResolvingPost] = useState(false);
  const draftUploadIdRef = useRef<string>('');

  const { data, loading, refetch } = useQuery(ADMIN_CONTESTS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const { data: contestDetailData } = useQuery(ADMIN_CONTEST_QUERY, {
    variables: { id: editContest?.id ?? '' },
    skip: !editContest?.id,
    fetchPolicy: 'cache-and-network',
  });

  const [communityPostBySlug] = useLazyQuery(GET_COMMUNITY_POST_BY_SLUG);
  const [adminCreateContest, { loading: creating }] = useMutation(ADMIN_CREATE_CONTEST);
  const [adminUpdateContest, { loading: updating }] = useMutation(ADMIN_UPDATE_CONTEST);
  const [adminDeleteContest, { loading: deleting }] = useMutation(ADMIN_DELETE_CONTEST);
  const [uploadContestImage] = useMutation(UPLOAD_CONTEST_IMAGE);
  const [processContestImage] = useMutation(PROCESS_CONTEST_IMAGE);

  const contests: Contest[] = data?.adminContests || [];
  const contestDetail = contestDetailData?.adminContest;

  const isActive = (contest: Contest) => contest.status === 'active' && new Date(contest.endDate) > new Date();

  const openCreateDialog = () => {
    draftUploadIdRef.current = crypto.randomUUID();
    setFormData(emptyForm());
    setResolvedPostId(null);
    setCreateDialogOpen(true);
  };

  const openEditDialog = (contest: Contest) => {
    draftUploadIdRef.current = crypto.randomUUID();
    const postUrl =
      contest.post?.slug && contest.post?.community?.slug
        ? `${__TOWN_SQUARE_URL__}/community/${contest.post.community.slug}/post/${contest.post.slug}`
        : '';
    setFormData({
      name: contest.name,
      description: contest.description || '',
      postUrl,
      endDate: new Date(contest.endDate),
      thumbnailUrl: contest.thumbnailUrl || '',
    });
    setResolvedPostId(contest.postId ?? null);
    setEditContest(contest);
  };

  const closeDialog = () => {
    setCreateDialogOpen(false);
    setEditContest(null);
    setResolvedPostId(null);
  };

  const handlePostUrlBlur = async () => {
    const parsed = parsePostUrl(formData.postUrl);
    if (!parsed) {
      setResolvedPostId(null);
      return;
    }
    setResolvingPost(true);
    try {
      const result = await communityPostBySlug({
        variables: { communitySlug: parsed.communitySlug, postSlug: parsed.postSlug },
      });
      const postId = result.data?.communityPostBySlug?.id;
      if (postId) {
        setResolvedPostId(postId);
      } else {
        setResolvedPostId(null);
        toast.error(t('contests-page.toast.post-not-found'));
      }
    } catch {
      setResolvedPostId(null);
    } finally {
      setResolvingPost(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error(t('contests-page.toast.name-required'));
      return;
    }
    if (!formData.endDate) {
      toast.error(t('contests-page.toast.end-date-required'));
      return;
    }
    if (!resolvedPostId) {
      toast.error(t('contests-page.toast.post-url-required'));
      return;
    }
    try {
      if (editContest) {
        await adminUpdateContest({
          variables: {
            input: {
              id: editContest.id,
              name: formData.name.trim(),
              description: formData.description.trim() || null,
              postId: resolvedPostId,
              endDate: formData.endDate.toISOString(),
              ...(formData.thumbnailUrl && { thumbnailUrl: formData.thumbnailUrl }),
            },
          },
        });
        toast.success(t('contests-page.toast.updated'));
      } else {
        await adminCreateContest({
          variables: {
            input: {
              name: formData.name.trim(),
              description: formData.description.trim() || null,
              postId: resolvedPostId,
              endDate: formData.endDate.toISOString(),
              ...(formData.thumbnailUrl && { thumbnailUrl: formData.thumbnailUrl }),
            },
          },
        });
        toast.success(t('contests-page.toast.created'));
      }
      closeDialog();
      refetch();
    } catch (err: any) {
      toast.error(err.message || t('contests-page.toast.save-failed'));
    }
  };

  const handleDelete = async () => {
    if (!deleteContest) return;
    try {
      await adminDeleteContest({ variables: { id: deleteContest.id } });
      toast.success(t('contests-page.toast.deleted'));
      setDeleteContest(null);
      refetch();
    } catch (err: any) {
      toast.error(err.message || t('contests-page.toast.delete-failed'));
    }
  };

  const contestUploader = useMemo(
    () =>
      profileImageUploader({
        presign: async (file) => {
          const uploadId = draftUploadIdRef.current;
          const signed = (await uploadContestImage({ variables: { uploadId, contentType: file.type } })).data
            ?.uploadContestImage;
          if (!signed?.url) throw new Error('Failed to get upload URL');
          return { url: signed.url, key: new URL(signed.url).pathname.substring(1) };
        },
        process: async (key) => {
          const processed = (
            await processContestImage({
              variables: {
                uploadId: draftUploadIdRef.current,
                key,
                contestId: editContest?.id ?? null,
              },
            })
          ).data?.processContestImage;
          if (!processed?.url) throw new Error('Failed to process image');
          return processed.url as string;
        },
      }),
    [editContest?.id, processContestImage, uploadContestImage]
  );

  const existingThumbnail = useMemo(
    () =>
      formData.thumbnailUrl
        ? [
            {
              id: `contest:${formData.thumbnailUrl}`,
              url: formData.thumbnailUrl,
              kind: 'image' as const,
              name: 'Contest thumbnail',
            },
          ]
        : [],
    [formData.thumbnailUrl]
  );

  const dialogContest = editContest ?? null;
  const isCreate = createDialogOpen && !editContest;
  const currentWinners: Record<number, ContestWinner | undefined> = {};
  (contestDetail?.winners ?? editContest?.winners ?? []).forEach((w: ContestWinner) => {
    currentWinners[w.place] = w;
  });

  if (loading && !data) {
    return (
      <AdminLayout title={t('contests-page.page-title')} description={t('contests-page.page-description')}>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title={t('contests-page.page-title')}
      description={t('contests-page.page-description')}
      action={
        <Button onClick={openCreateDialog} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          {t('contests-page.create-contest')}
        </Button>
      }
    >
      <div className="bg-white rounded-md border overflow-hidden">
        <div className="bg-zinc-50 border-b">
          <div className="grid grid-cols-[60px_1fr_120px_160px_80px_100px] py-3 px-4 text-xs font-medium">
            <div>{t('contests-page.table.thumbnail')}</div>
            <div>{t('contests-page.table.name')}</div>
            <div className="text-center">{t('contests-page.table.status')}</div>
            <div>{t('contests-page.table.end-date')}</div>
            <div className="text-center">{t('contests-page.table.winners')}</div>
            <div className="text-right">{t('contests-page.table.actions')}</div>
          </div>
        </div>
        <div>
          {contests.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>{t('contests-page.empty.title')}</p>
              <p className="text-sm mt-2">{t('contests-page.empty.hint')}</p>
            </div>
          ) : (
            contests.map((contest) => (
              <div key={contest.id} className="border-b last:border-b-0">
                <div className="grid grid-cols-[60px_1fr_120px_160px_80px_100px] items-center px-4 py-3 text-sm hover:bg-gray-50">
                  <div>
                    {contest.thumbnailUrl ? (
                      <img
                        src={contest.thumbnailUrl}
                        alt={contest.name}
                        className="w-10 h-10 rounded object-cover border border-gray-200"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center text-gray-400 text-xs">
                        —
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="font-medium">{contest.name}</p>
                    {contest.description && (
                      <p className="text-xs text-gray-500 truncate max-w-xs" title={contest.description}>
                        {contest.description}
                      </p>
                    )}
                  </div>
                  <div className="text-center">
                    {isActive(contest) ? (
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                        {t('contests-page.status-active')}
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                        {t('contests-page.status-ended')}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600">
                    {new Date(contest.endDate).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                  <div className="text-center text-sm text-gray-600">{contest.winners.length} / 3</div>
                  <div className="flex justify-end gap-1">
                    {contest.post?.community?.slug && contest.post?.slug && (
                      <Button
                        variant="ghost"
                        size="sm"
                        asChild
                        className="h-8 w-8 p-0"
                        aria-label={t('contests-page.table.view-post')}
                      >
                        <a
                          href={`${__TOWN_SQUARE_URL__}/community/${contest.post.community.slug}/post/${contest.post.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(contest)} className="h-8 w-8 p-0">
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteContest(contest)}
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={createDialogOpen || !!dialogContest} onOpenChange={closeDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {dialogContest ? t('contests-page.dialog.title-edit') : t('contests-page.dialog.title-create')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">{t('contests-page.form.name-label')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('contests-page.form.name-placeholder')}
                required
              />
            </div>
            <div>
              <Label htmlFor="description">{t('contests-page.form.description-label')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('contests-page.form.description-placeholder')}
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="postUrl">{t('contests-page.form.post-url-label')}</Label>
              <div className="relative">
                <Input
                  id="postUrl"
                  value={formData.postUrl}
                  onChange={(e) => {
                    setFormData({ ...formData, postUrl: e.target.value });
                    setResolvedPostId(null);
                  }}
                  onBlur={handlePostUrlBlur}
                  placeholder={t('contests-page.form.post-url-placeholder')}
                  required
                />
                {resolvingPost && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">{t('contests-page.form.post-url-hint')}</p>
            </div>
            <div>
              <Label>{t('contests-page.form.end-date-label')}</Label>
              <DateTimePicker
                date={formData.endDate}
                onDateChange={(date) => setFormData({ ...formData, endDate: date })}
                disablePastDates={false}
              />
            </div>

            {/* Thumbnail upload — available in both create and edit modes */}
            <div>
              <Label>{t('contests-page.form.thumbnail-label')}</Label>
              {formData.thumbnailUrl && (
                <div className="mb-2">
                  <img
                    src={formData.thumbnailUrl}
                    alt="Contest thumbnail"
                    className="w-20 h-20 rounded-lg object-cover border border-gray-200"
                  />
                </div>
              )}
              <MediaDropRegion
                width="100%"
                tone="neutral"
                prompt={t('contests-page.form.thumbnail-label')}
                hint="JPG, PNG, WebP · 10 MB"
                accept={['image/jpeg', 'image/png', 'image/webp']}
                maxSizeMB={10}
                multiple={false}
                existing={existingThumbnail}
                uploader={contestUploader}
                onItemComplete={(item: UploadItem) => {
                  setFormData((current) => ({ ...current, thumbnailUrl: item.result as string }));
                  toast.success(t('contests-page.toast.image-uploaded'));
                }}
                onItemError={(_, error) => toast.error(error.message || t('contests-page.toast.image-upload-failed'))}
                onRemove={(item: UploadItem) => {
                  if (item.phase === 'done') setFormData((current) => ({ ...current, thumbnailUrl: '' }));
                }}
              />
              {isCreate && <p className="text-xs text-gray-500">{t('contests-page.form.thumbnail-create-hint')}</p>}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                {tc('cancel')}
              </Button>
              <Button type="submit" disabled={creating || updating}>
                {creating || updating ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{tc('saving')}</span>
                  </div>
                ) : (
                  <span>
                    {dialogContest ? t('contests-page.form.update-contest') : t('contests-page.create-contest')}
                  </span>
                )}
              </Button>
            </DialogFooter>
          </form>

          {/* Winners — read-only display. Picking is done from the comment menu in town-square. */}
          {dialogContest && (
            <div className="mt-6 border-t pt-4 space-y-4">
              <h3 className="font-medium text-sm">{t('contests-page.winners.title')}</h3>
              <div className="grid grid-cols-3 gap-3">
                {PLACES.map((place) => {
                  const winner = currentWinners[place];
                  const displayName = winner ? getWinnerDisplayName(winner) : null;
                  const imageSrc = winner ? extractFirstImageSrc(winner.comment.content) : null;
                  const profileSlug = winner?.user.slug;
                  const profileUrl = profileSlug ? `${__TOWN_SQUARE_URL__}/${profileSlug}` : null;
                  const postCommunitySlug = dialogContest?.post?.community?.slug;
                  const postSlug = dialogContest?.post?.slug;
                  const commentUrl =
                    winner && postCommunitySlug && postSlug
                      ? `${__TOWN_SQUARE_URL__}/community/${postCommunitySlug}/post/${postSlug}#comment-${winner.comment.id}`
                      : null;

                  const avatarNode = winner?.user.avatarUrl ? (
                    <img src={winner.user.avatarUrl} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gray-200 shrink-0" />
                  );

                  return (
                    <div key={place} className="border rounded-lg p-3 space-y-2">
                      <p className="text-xs font-medium text-gray-600">
                        {t(`contests-page.winners.${PLACE_LABELS[place]}`)}
                      </p>
                      {winner ? (
                        <div className="space-y-2">
                          {commentUrl ? (
                            <a
                              href={commentUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="block aspect-square w-full rounded bg-gray-100 overflow-hidden hover:opacity-90 transition-opacity"
                              aria-label={t('contests-page.winners.view-comment')}
                            >
                              {imageSrc ? (
                                <img src={imageSrc} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                  {t('contests-page.winners.no-image')}
                                </div>
                              )}
                            </a>
                          ) : (
                            <div className="aspect-square w-full rounded bg-gray-100 overflow-hidden">
                              {imageSrc ? (
                                <img src={imageSrc} alt="" className="w-full h-full object-cover" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400 text-xs">
                                  {t('contests-page.winners.no-image')}
                                </div>
                              )}
                            </div>
                          )}
                          {profileUrl ? (
                            <a
                              href={profileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 min-w-0 hover:underline"
                              title={displayName ?? undefined}
                            >
                              {avatarNode}
                              <span className="text-sm font-medium truncate">{displayName}</span>
                            </a>
                          ) : (
                            <div className="flex items-center gap-2 min-w-0" title={displayName ?? undefined}>
                              {avatarNode}
                              <span className="text-sm font-medium truncate">{displayName}</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="text-xs text-gray-400 italic">{t('contests-page.winners.not-selected')}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteContest} onOpenChange={() => setDeleteContest(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t('contests-page.delete-dialog.title')}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-600">{t('contests-page.delete-dialog.message')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteContest(null)}>
              {tc('cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{tc('deleting')}</span>
                </div>
              ) : (
                t('contests-page.delete-dialog.confirm')
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
